import { useState, useEffect, useCallback } from 'react';
import { folderService } from '../utilities/folder/FolderService';
import { Folder, FolderWithCount, CreateFolderData } from '../utilities/types/Folder';

export function useFolders() {
  const [folders, setFolders] = useState<FolderWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadFolders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await folderService.getAllWithCounts();
      setFolders(data);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  const createFolder = useCallback(async (data: CreateFolderData) => {
    const folder = await folderService.create(data);
    await loadFolders(); // Refresh list
    return folder;
  }, [loadFolders]);

  const updateFolder = useCallback(async (id: string, updates: Partial<Folder>) => {
    await folderService.update(id, updates);
    await loadFolders(); // Refresh list
  }, [loadFolders]);

  const deleteFolder = useCallback(async (id: string, moveToId?: string) => {
    await folderService.delete(id, moveToId);
    await loadFolders(); // Refresh list
  }, [loadFolders]);

  const reorderFolders = useCallback(async (folderIds: string[]) => {
    await folderService.reorder(folderIds);
    await loadFolders(); // Refresh list
  }, [loadFolders]);

  const syncFolders = useCallback(async () => {
    return await folderService.syncAllFoldersToServer();
  }, []);

  const moveBookmarkToFolder = useCallback(async (bookmarkId: string, folderId?: string) => {
    await folderService.moveBookmarkToFolder(bookmarkId, folderId);
  }, []);

  const classifyBookmark = useCallback(async (
    url: string,
    title?: string,
    description?: string
  ) => {
    const availableFolders = await folderService.getAll();
    return await folderService.classifyBookmarkToFolder(url, title, description, availableFolders);
  }, []);

  return {
    folders,
    loading,
    error,
    createFolder,
    updateFolder,
    deleteFolder,
    reorderFolders,
    syncFolders,
    moveBookmarkToFolder,
    classifyBookmark,
    refresh: loadFolders,
  };
}

